/**
 * sdk/browser-agent.ts
 *
 * Browser-compatible PineSeedAgent factory.
 *
 * Wires a live WebSocket (provided by the caller, cookie-authenticated) into
 * WsTransport and PineSeedAgent, returning the agent as acp.Agent directly.
 * No AgentSideConnection, no ndJsonStream, no stdio, no subprocess.
 *
 * The React app:
 *   1. Opens a cookie-authenticated WebSocket (no auth headers).
 *   2. Calls createBrowserAgent({ apiUrl, ws, participantId, client, loadSessionTimeoutMs }).
 *   3. Receives BrowserAgent — calls .agent.initialize(), .agent.newSession(), .agent.prompt(), etc.
 *
 * Constraints:
 *   - No auth headers — browser WebSocket API does not support them; cookie auth only.
 *   - No API key on fetch calls — capabilities endpoint uses credentials: "include".
 *   - No defaults, no fallbacks, no hardcoding. Fail-fast on every invalid input.
 */

import * as acp from "@agentclientprotocol/sdk";
import { PineSeedAgent } from "../src/agent";
import type { PromptPipelineFactory, ScopeResolver } from "../src/agent";
import { WsTransport } from "@pineseed/transport/src/ws-transport";
import { assertHttpOk } from "../src/http-errors";
import { PromptPipeline, UpdatePipeline } from "../src/pipeline";
import { makeScopeResolver } from "../src/scope-resolver";
import { mimeToExt, extToMime } from "@pineseed/spec/mime";
import { makeReferenceResolver } from "@pineseed/spec/reference-resolver";
import type { BrowserTerminalHandle } from "./terminal";

// ─── Capabilities path ────────────────────────────────────────────────────────

const ACP_CAPABILITIES_PATH = "/api/agency/acp/capabilities";

// ─── Public config ────────────────────────────────────────────────────────────

export interface BrowserAgentConfig {
  /**
   * Base URL of the PineSeed API, e.g. "https://api.pineseed.com".
   * Used to fetch capabilities. Must not have a trailing slash.
   */
  apiUrl: string;

  /**
   * Live, already-open WebSocket connected to the ACP endpoint.
   * Must be in OPEN state (readyState === WebSocket.OPEN).
   * Cookie authentication must already be in effect on the connection.
   */
  ws: WebSocket;

  /**
   * Participant ID of the current user.
   */
  participantId: string;

  /**
   * Provider location label for tool registration.
   * Identifies this browser session's origin (e.g. workspace slug, user handle).
   * Must satisfy ^[a-z0-9][a-z0-9_-]*[a-z0-9]$.
   */
  location: string;

  /**
   * ACP client implementation provided by the React app.
   * Receives all agent→client requests and notifications in-process.
   *
   * If the app does not advertise terminal capability during initialize(),
   * createTerminal will never be called and may be omitted.
   *
   * If terminal capability is advertised, createTerminal must return a
   * BrowserTerminalHandle — see sdk/terminal.ts.
   */
  client: BrowserClient;

  /**
   * Timeout in milliseconds for session/load history replay.
   * The caller must determine an appropriate value; no default is applied.
   */
  loadSessionTimeoutMs: number;

  /**
   * Public app base URL for resolving psref:/// references in outbound updates.
   * e.g. "https://app.pineseed.com" — no trailing slash.
   */
  appUrl: string;

  /**
   * Signed URL TTL in seconds for storage uploads.
   * Sourced from PINESEED_ACP_UPLOAD_TTL_S (or equivalent browser env).
   */
  uploadTtlS: number;

  /**
   * Optional extra headers injected into all HTTP requests
   * (capabilities fetch and auth headers passed to pipelines).
   * e.g. { "X-Workspace-Key": "wk_abc123" }.
   */
  extraHeaders?: Record<string, string>;
}

// ─── BrowserClient ────────────────────────────────────────────────────────────
//
// Extends acp.Client with a createTerminal override that returns BrowserTerminalHandle
// instead of CreateTerminalResponse. This is the type the React app implements.
//
// All other optional Client methods (readTextFile, writeTextFile, extMethod, etc.)
// are inherited unchanged from acp.Client.

export interface BrowserClient extends Omit<acp.Client, "createTerminal"> {
  createTerminal?(
    params: acp.CreateTerminalRequest,
  ): Promise<BrowserTerminalHandle>;
  /**
   * Dispatches a custom tool call registered via clientCapabilities._customTools.
   * The browser implementation routes by toolName and executes the appropriate action.
   * Errors propagate naturally — are caught by PineSeedAgent and returned as isError=true ToolResult.
   */
  handleCustomTool(
    toolName: string,
    args: Record<string, unknown>,
  ): Promise<Record<string, unknown>>;
}

// ─── Result ───────────────────────────────────────────────────────────────────

export interface BrowserAgent {
  /**
   * The ACP agent — PineSeedAgent implementing acp.Agent directly.
   * Call .initialize(), .newSession(), .prompt(), .cancel(), etc.
   */
  agent: acp.Agent;

  /**
   * Disconnect the underlying WebSocket transport cleanly.
   * Call on component unmount or session teardown.
   */
  disconnect(): Promise<void>;
}

// ─── Factory ──────────────────────────────────────────────────────────────────

/**
 * Creates a browser-compatible PineSeedAgent wired to the given WebSocket.
 *
 * Fetches capabilities from the API (cookie-authenticated via credentials: "include"),
 * constructs WsTransport around the provided live WebSocket via injectWebSocket(),
 * and returns a ready acp.Agent the React app can call directly.
 *
 * Throws immediately if:
 *   - apiUrl is empty
 *   - ws is not in OPEN state
 *   - participantId is empty
 *   - loadSessionTimeoutMs is not a finite positive number
 *   - capabilities fetch fails or returns non-2xx
 */
export async function createBrowserAgent(
  config: BrowserAgentConfig,
): Promise<BrowserAgent> {
  // ── Fail-fast validation ──────────────────────────────────────────────────

  if (!config.apiUrl) {
    throw new Error("[BrowserAgent] apiUrl is required");
  }
  if (config.ws.readyState !== WebSocket.OPEN) {
    throw new Error(
      `[BrowserAgent] WebSocket must be in OPEN state (readyState=${config.ws.readyState})`,
    );
  }
  if (!config.participantId) {
    throw new Error("[BrowserAgent] participantId is required");
  }
  if (
    !Number.isFinite(config.loadSessionTimeoutMs) ||
    config.loadSessionTimeoutMs <= 0
  ) {
    throw new Error(
      "[BrowserAgent] loadSessionTimeoutMs must be a finite positive number",
    );
  }

  // ── Fetch capabilities (cookie-authenticated) ─────────────────────────────

  const capabilitiesUrl = `${config.apiUrl}${ACP_CAPABILITIES_PATH}`;
  const capabilitiesRes = await fetch(capabilitiesUrl, {
    credentials: "include",
    headers: { ...config.extraHeaders },
  });
  await assertHttpOk(capabilitiesRes, capabilitiesUrl);
  const capabilities = (await capabilitiesRes.json()) as acp.InitializeResponse;

  // ── WsTransport — injected with the caller-provided live WebSocket ─────────
  // auth and extraHeaders are not passed — browser relies on cookies.
  // connect() is never called; injectWebSocket() attaches the live socket directly.

  const transport = new WsTransport({
    url: config.ws.url,
    capabilities,
    loadSessionTimeoutMs: config.loadSessionTimeoutMs,
  });

  transport.injectWebSocket(config.ws);

  // ── Pipeline — cookie auth, no API key headers ────────────────────────────
  // Browser WebSocket auth is cookie-based; all API fetches use credentials: "include".
  // authHeaders is empty — the browser sends cookies automatically.

  const authHeaders = { ...config.extraHeaders };

  const scopeResolver: ScopeResolver = makeScopeResolver(
    config.apiUrl,
    authHeaders,
    { credentials: "include" },
  );

  const promptPipelineFactory: PromptPipelineFactory = (
    sessionId,
    workspaceId,
    scope,
  ) =>
    new PromptPipeline({
      apiUrl: config.apiUrl,
      authHeaders,
      fetchOptions: { credentials: "include" },
      fetchApi: fetch,
      uploadTtlS: config.uploadTtlS,
      mimeResolver: extToMime,
      mimeToExtResolver: mimeToExt,
      sessionId,
      workspaceId,
      participantId: config.participantId,
      scope,
    });

  const updatePipeline = new UpdatePipeline({
    mimeResolver: extToMime,
    resolver: makeReferenceResolver(config.appUrl),
  });

  // ── PineSeedAgent — acp.Agent, called directly by the React app ───────────

  const agent = new PineSeedAgent(
    makeBrowserClientProxy(config.client),
    transport,
    transport,
    {
      promptPipelineFactory,
      scopeResolver,
      updatePipeline,
      participantId: config.participantId,
      location: config.location,
    },
  );

  return {
    agent,
    disconnect: () => transport.disconnect(),
  };
}

// ─── In-process client proxy ──────────────────────────────────────────────────
//
// PineSeedAgent holds an AgentSideConnection and calls its methods when upstream
// messages arrive from the backend (sessionUpdate, requestPermission, readTextFile,
// writeTextFile, createTerminal, extMethod, extNotification).
//
// In the browser/direct path there is no AgentSideConnection. This proxy satisfies
// the structural shape PineSeedAgent requires, forwarding every call directly to
// the caller-supplied BrowserClient in-process.
//
// Only the methods PineSeedAgent actually invokes on its connection are declared.
// Any unexpected call throws immediately so omissions are caught at runtime.

interface AgentConnectionProxy {
  sessionUpdate(params: acp.SessionNotification): Promise<void>;
  requestPermission(
    params: acp.RequestPermissionRequest,
  ): Promise<acp.RequestPermissionResponse>;
  readTextFile(
    params: acp.ReadTextFileRequest,
  ): Promise<acp.ReadTextFileResponse>;
  writeTextFile(
    params: acp.WriteTextFileRequest,
  ): Promise<acp.WriteTextFileResponse>;
  createTerminal(
    params: acp.CreateTerminalRequest,
  ): Promise<BrowserTerminalHandle>;
  handleCustomTool(
    toolName: string,
    args: Record<string, unknown>,
  ): Promise<Record<string, unknown>>;
  extMethod(
    method: string,
    params: Record<string, unknown>,
  ): Promise<Record<string, unknown>>;
  extNotification(
    method: string,
    params: Record<string, unknown>,
  ): Promise<void>;
}

function makeBrowserClientProxy(client: BrowserClient): AgentConnectionProxy {
  return {
    sessionUpdate(params: acp.SessionNotification): Promise<void> {
      return Promise.resolve(client.sessionUpdate(params));
    },

    requestPermission(
      params: acp.RequestPermissionRequest,
    ): Promise<acp.RequestPermissionResponse> {
      return Promise.resolve(client.requestPermission(params));
    },

    readTextFile(
      params: acp.ReadTextFileRequest,
    ): Promise<acp.ReadTextFileResponse> {
      if (!client.readTextFile) {
        throw new Error(
          "[BrowserAgent] agent requested fs/read_text_file but client does not implement readTextFile",
        );
      }
      return Promise.resolve(client.readTextFile(params));
    },

    writeTextFile(
      params: acp.WriteTextFileRequest,
    ): Promise<acp.WriteTextFileResponse> {
      if (!client.writeTextFile) {
        throw new Error(
          "[BrowserAgent] agent requested fs/write_text_file but client does not implement writeTextFile",
        );
      }
      // ACP permits a void return; WriteTextFileResponse has only optional fields.
      return Promise.resolve(client.writeTextFile(params)).then(
        (res) => res ?? {},
      );
    },

    createTerminal(
      params: acp.CreateTerminalRequest,
    ): Promise<BrowserTerminalHandle> {
      if (!client.createTerminal) {
        throw new Error(
          "[BrowserAgent] agent requested terminal/create but client does not implement createTerminal",
        );
      }
      return client.createTerminal(params);
    },

    handleCustomTool(
      toolName: string,
      args: Record<string, unknown>,
    ): Promise<Record<string, unknown>> {
      return client.handleCustomTool(toolName, args);
    },

    extMethod(
      method: string,
      params: Record<string, unknown>,
    ): Promise<Record<string, unknown>> {
      if (!client.extMethod) {
        throw new Error(
          `[BrowserAgent] agent sent extension method "${method}" but client does not implement extMethod`,
        );
      }
      return Promise.resolve(client.extMethod(method, params));
    },

    extNotification(
      method: string,
      params: Record<string, unknown>,
    ): Promise<void> {
      if (!client.extNotification) {
        throw new Error(
          `[BrowserAgent] agent sent extension notification "${method}" but client does not implement extNotification`,
        );
      }
      return Promise.resolve(client.extNotification(method, params));
    },
  };
}
